<script setup>
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { useAlert as wgptUseAlert } from 'dashboard/composables/wgpt';
import { useAlert } from 'dashboard/composables';

import SnackbarContainer from 'next/wgpt/Snackbar/SnackbarContainer.vue';
import Dialog from 'next/dialog/Dialog.vue';
import EditWorkflowForm from './EditWorkflowForm.vue';

const emit = defineEmits(['close']);
const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();

const dialogRef = ref(null);
const isOpen = ref(false);
const bot = ref({});
const workflow = ref({});
const uiFlags = getters['wgptBotWorkflows/getUIFlags'];

const title = computed(() => {
  return (
    t('WGPT_BOTS.EDIT.TABS.WORKFLOWS.EDIT.TITLE') + ' ' + workflow.value.name
  );
});

const onClose = () => {
  isOpen.value = false;
  emit('close');
};

const formRef = ref(null);

const open = (selectedBot, selectedWorkflow) => {
  bot.value = selectedBot;
  workflow.value = selectedWorkflow;
  isOpen.value = true;
  nextTick(() => {
    if (!dialogRef.value) return;
    dialogRef.value.open();
  });
};

const close = () => {
  dialogRef.value.close();
  onClose();
};

const handleSubmit = async () => {
  try {
    if (!formRef.value) return;
    const { validate } = formRef.value;

    const data = await validate();
    if (!data) return;

    const workflowUpdates = {
      ...data,
    };
    await store.dispatch('wgptBotWorkflows/update', {
      ...workflowUpdates,
      botId: bot.value.id,
      id: workflow.value.id,
    });

    useAlert(t('WGPT_BOTS.EDIT.TABS.WORKFLOWS.EDIT.API.SUCCESS_MESSAGE'));
    close();
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      t('WGPT_BOTS.EDIT.TABS.WORKFLOWS.EDIT.API.ERROR_MESSAGE');
    wgptUseAlert(errorMessage);
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="title"
    :confirm-button-label="t('WGPT_BOTS.EDIT.TABS.WORKFLOWS.EDIT.SAVE')"
    :cancel-button-label="t('WGPT_BOTS.EDIT.TABS.WORKFLOWS.EDIT.CANCEL')"
    :is-loading="uiFlags.isUpdating"
    :disable-confirm-button="uiFlags.isUpdating"
    width="2xl"
    @confirm="handleSubmit"
    @close="onClose"
  >
    <SnackbarContainer />
    <div
      v-if="isOpen"
      class="max-h-[calc(100vh-14rem)] overflow-y-auto -mx-6 px-6"
    >
      <EditWorkflowForm
        ref="formRef"
        :workflow="workflow"
        @submit="handleSubmit"
      />
    </div>
  </Dialog>
</template>
